<template>
  <v-container fluid>
    <!-- Action bar — hidden when printing -->
    <div class="no-print action-bar">
      <span class="action-bar__title">พิมพ์บัตรพนักงาน ({{ employees.length }} บัตร)</span>
      <v-btn
        color="primary"
        elevation="2"
        :loading="loading"
        @click="handlePrint">
        <v-icon left>
          mdi-printer
        </v-icon>
        พิมพ์บัตรทั้งหมด
      </v-btn>
    </div>

    <!-- Empty state -->
    <div
      v-if="!loading && employees.length === 0"
      class="text-center py-16 no-print">
      <v-icon
        size="72"
        color="grey lighten-2">
        mdi-card-account-details-outline
      </v-icon>
      <div class="text-h6 grey--text mt-4">
        ไม่มีพนักงานที่เลือก
      </div>
      <div class="caption grey--text mt-1">
        กรุณาปิดหน้าต่างนี้แล้วเลือกพนักงานก่อน
      </div>
    </div>

    <!-- Cards grid -->
    <div
      v-else
      class="cards-grid">
      <div
        v-for="emp in employees"
        :key="emp.id"
        class="id-card">
        <!-- ── Header ── -->
        <div class="id-card__header">
          <div class="id-card__company">
            GENTLEWOMAN
          </div>
          <div class="id-card__role">
            WAREHOUSE STAFF
          </div>
        </div>

        <!-- ── QR area (ใหญ่) ── -->
        <div class="id-card__qr-area">
          <img
            v-if="qrCodes[emp.id]"
            :src="qrCodes[emp.id]"
            class="id-card__qr-img"
            alt="QR"
          >
        </div>

        <!-- ── Bottom: photo + info ── -->
        <div class="id-card__bottom">
          <div class="id-card__info">
            <div class="id-card__name">
              {{ emp.firstName }} {{ emp.lastName }}
            </div>
            <div class="id-card__code">
              {{ emp.employeeCode }}
            </div>
            <div class="id-card__dept">
              Dept. {{ emp.department.toUpperCase() }}
            </div>
          </div>
          <div style="display: flex; align-items: center; justify-content: center; padding-right: 10px;">
            <div class="id-card__photo">
              <v-icon
                size="36"
                color="grey lighten-1">
                mdi-account
              </v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import QRCode from 'qrcode'
import { mapGetters } from 'vuex'

export default {
  data: () => ({
    loading: false,
    qrCodes: {}
  }),
  computed: {
    ...mapGetters({
      employees: 'WarehouseWorkTracker/selectedEmployees'
    })
  },
  async mounted () {
    this.loading = true
    await this.generateQRCodes()
    this.loading = false
  },
  methods: {
    async generateQRCodes () {
      const codes = {}
      await Promise.all(this.employees.map(async (emp) => {
        try {
          const url = await QRCode.toDataURL(emp.qrCodeValue || emp.employeeCode || String(emp.id), {
            width: 300,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            }
          })
          codes[emp.id] = url
        } catch (err) {
          console.error('QR generation failed for', emp.employeeCode, err)
        }
      }))
      this.qrCodes = codes
    },
    handlePrint () {
      window.print()
    }
  }
}
</script>

<style scoped>
/* ── Action bar ── */
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 8mm;
}

.action-bar__title {
  font-size: 16px;
  font-weight: 600;
  color: #424242;
}

/* ── Grid ── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 54mm);
  gap: 6mm;
  width: fit-content;
  margin: 0 auto;
  padding: 4mm;
}

/* ── Card shell: 54 × 85.6mm (ISO ID-1 portrait) ── */
.id-card {
  width: 54mm;
  height: 85.6mm;
  padding-top: 3mm;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 1mm 4mm rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  border: 1px solid #cccccc;
}

/* ── Header: 12mm ── */
.id-card__header {
  flex-shrink: 0;
  height: 12mm;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 3mm;
  gap: 0.5mm;
}

.id-card__company {
  font-size: 12pt;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: #000000;
}

.id-card__role {
  font-size: 6pt;
  font-weight: 400;
  letter-spacing: 2px;
  color: #555555;
}

/* ── QR area (ใหญ่): flex:1 (~48mm) ── */
.id-card__qr-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-bottom: 0.3mm solid #cccccc;
  padding: 2mm;
}

.id-card__qr-img {
  display: block;
  width: 40mm;
  height: 40mm;
}

/* ── Bottom bar: 28mm ── */
.id-card__bottom {
  flex-shrink: 0;
  height: 28mm;
  display: flex;
}

/* Photo (avatar) section */
.id-card__photo {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border: 1px solid #cccccc;
  border-radius: 100%;
}

/* Info section */
.id-card__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2mm 2mm 2mm 3mm;
  gap: 1mm;
  min-width: 0;
}

.id-card__name {
  font-size: 9pt;
  font-weight: 700;
  color: #000000;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.id-card__code {
  font-size: 5.5pt;
  color: #555555;
  line-height: 1;
  letter-spacing: 0.8px;
}

.id-card__dept {
  font-size: 5.5pt;
  font-weight: 600;
  color: #000000;
  display: inline-block;
  width: fit-content;
}
</style>

<!-- Global print styles -->
<style>
@media print {
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  .no-print {
    display: none !important;
  }

  .v-main {
    padding: 0 !important;
  }

  .cards-grid {
    padding: 0 !important;
    gap: 5mm !important;
  }

  .id-card {
    break-inside: avoid;
    box-shadow: none !important;
  }
}
</style>
